<template>
  <div class="my-devices">
    <!-- 查询条件区域 -->
    <div class="query-section">
      <div class="query-form">
        <div class="form-item">
          <label class="form-label">MAD：</label>
          <el-input
            v-model="queryParams.mad"
            placeholder="请输入"
            clearable
            class="input-field"
          />
        </div>
        <div class="form-item">
          <label class="form-label">出厂日期：</label>
          <el-date-picker
            v-model="queryParams.productionDate"
            type="date"
            placeholder="请选择日期"
            value-format="YYYY-MM-DD"
            class="input-field"
          />
        </div>
        <div class="button-group">
          <el-button class="reset-btn" @click="handleReset">重置</el-button>
          <el-button type="primary" class="query-btn" @click="handleQuery"
            >查询</el-button
          >
        </div>
      </div>
    </div>

    <!-- 数据展示区域 -->
    <div class="data-section">
      <div class="section-header">
        <div>
          <span style="font-size: 14px; color: #333">我导入的设备</span>
        </div>
        <div class="toolbar">
          <!-- 刷新按钮 -->
          <el-button type="text" @click="refreshData">
              <el-icon><Refresh /></el-icon>
            </el-button>

          <!-- 密度设置 -->
          <el-popover
            placement="bottom"
            width="160"
            v-model:visible="densityDialogVisible"
          >
            <div class="popover-content">
              <el-button
                class="density-button"
                :type="rowHeight === '80px' ? 'primary' : ''"
                @click="setRowHeight('80px')"
              >
                默认
              </el-button>
              <el-button
                class="density-button"
                :type="rowHeight === '60px' ? 'primary' : ''"
                @click="setRowHeight('60px')"
              >
                中等
              </el-button>
              <el-button
                class="density-button"
                :type="rowHeight === '30px' ? 'primary' : ''"
                @click="setRowHeight('30px')"
              >
                紧凑
              </el-button>
            </div>
            <template #reference>
              <el-button type="text">
                <el-icon><Grid /></el-icon>
              </el-button>
            </template>
          </el-popover>

          <!-- 列显示设置 -->
          <el-popover
            placement="bottom"
            width="200"
            v-model:visible="columnsDialogVisible"
          >
            <div class="popover-content">
              <el-checkbox-group v-model="tempSelectedColumns">
                <el-checkbox
                  v-for="column in columns"
                  :key="column.prop"
                  :label="column.prop"
                >
                  {{ column.label }}
                </el-checkbox>
              </el-checkbox-group>
              <div class="popover-footer">
                <el-button @click="resetColumns">重置</el-button>
                <el-button type="primary" @click="confirmColumns">确定</el-button>
              </div>
            </div>
            <template #reference>
              <el-button type="text">
                <el-icon><Setting /></el-icon>
              </el-button>
            </template>
          </el-popover>
        </div>
      </div>

      <!-- 数据表格 -->
      <el-table
        :data="tableData"
        :row-class-name="tableRowClassName"
        :row-style="getRowStyle"
        :header-cell-style="{
          background: '#f5f7fa',
          color: '#909399',
          textAlign: 'center',
        }"
        :cell-style="{ textAlign: 'center' }"
        style="width: 100%"
      >
        <el-table-column
          v-for="column in visibleColumns"
          :key="column.prop"
          :prop="column.prop"
          :label="column.label"
          :filters="column.filters"
          :filter-method="column.filterMethod"
          :filter-placement="column.filterPlacement"
          :align="column.align || 'center'"
        >
          <template #header="scope">
            {{ scope.column.label }}
            <i
              v-if="column.filters"
              class="el-icon-filter"
              style="cursor: pointer; color: #409eff"
              @click.stop="showFilter(column.prop)"
            ></i>
          </template>
          <template #default="scope">
            <template v-if="column.prop === 'connectionStatus'">
              <span>
                <i :class="`status-dot ${getStatusClass(scope.row[column.prop])}`"></i>
                {{ scope.row[column.prop] === 0 ? '未连接' : '已连接' }}
              </span>
            </template>
            <template v-else>
              {{ scope.row[column.prop] }}
            </template>
          </template>
        </el-table-column>

        <template #empty>
          <div class="empty-state">
            <el-icon class="empty-icon"><Box /></el-icon>
            <p class="empty-text">暂无数据</p>
          </div>
        </template>
      </el-table>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import { ElMessage } from "element-plus";
import { getImportedDevices } from "@/api/device";
import { Grid, Setting, Refresh, Box } from "@element-plus/icons-vue";

export default {
  name: "MyDevices",
  components: {
    Grid,
    Setting,
    Refresh,
    Box,
  },
  setup() {
    // 查询参数
    const queryParams = ref({
      mad: "",
      productionDate: "",
    });

    const userRole = ref(null); // 用户角色
    const loading = ref(false); // 加载状态
    const tableData = ref([]); // 表格数据

    // 初始化时获取用户角色和数据
    onMounted(async () => {
      const userInfo = localStorage.getItem('userInfo');
      if (userInfo) {
        const user = JSON.parse(userInfo);
        userRole.value = user.role; // 0: 管理员, 1: 员工, 2: 维修工程师
      }
      await refreshData(false);
    });

    // 刷新数据
    const refreshData = async (isShow) => {
      loading.value = true;
      try {
        const response = await getImportedDevices(queryParams.value);
        if (response.data && response.success) {
          tableData.value = response.data || [];
          if(isShow)
            ElMessage.success("数据已刷新");
        } else {
          ElMessage.error(response?.message || "获取数据失败");
        }
      } catch (error) {
        console.error("刷新数据失败:", error);
        ElMessage.error(error.message || "刷新数据失败");
      } finally {
        loading.value = false;
      }
    };

    // 查询处理
    const handleQuery = async () => {
      await refreshData(true);
    };

    // 重置处理
    const handleReset = () => {
      queryParams.value = {
        mad: "",
        productionDate: "",
      };
      refreshData(true);
    };

    // 表格配置
    const columns = ref([
      { prop: "index", label: "序号", visible: true },
      { prop: "mad", label: "MAD", visible: true },
      { prop: "productionDate", label: "出厂日期", visible: true },
      { prop: "deviceType", label: "设备类型", visible: true },
      {
        prop: "connectionStatus",
        label: "设备连接状态",
        visible: true,
        filters: [
          { text: "在线", value: 0 },
          { text: "离线", value: 1 },
        ],
        filterMethod: (value, row) => row.connectionStatus === value,
        filterPlacement: "bottom-end",
      },
    ]);

    const selectedColumns = ref(columns.value.map((col) => col.prop));

    const tempSelectedColumns = ref([...selectedColumns.value]);

    // 计算属性：过滤可见的列
    const visibleColumns = computed(() =>
      columns.value.filter((col) => selectedColumns.value.includes(col.prop))
    );

    // 行高配置
    const rowHeight = ref("80px");
    const densityDialogVisible = ref(false);
    const columnsDialogVisible = ref(false);



    // 状态样式
    const getStatusClass = (status) => {
      switch (status) {
        case 1:
          return "status-dot-green";
        case 0:
        default:
          return "status-dot-red";
      }
    };

    // 表格样式
    const getRowStyle = () => ({
      height: rowHeight.value,
      transition: "height 0.3s ease",
    });

    const tableRowClassName = ({ rowIndex }) =>
      rowIndex % 2 === 0 ? "even-row" : "odd-row";

    // 密度设置
    const setRowHeight = (height) => {
      rowHeight.value = height;
      densityDialogVisible.value = false;
    };

    // 列显示设置
    const resetColumns = () => {
      // 根据用户角色决定是否显示操作列
      if (userRole.value === 1 || userRole.value === 2) {
        // 员工和维修工程师不显示操作列
        tempSelectedColumns.value = columns.value
          .filter(col => col.prop !== 'operation')
          .map(col => col.prop);
      } else {
        // 管理员显示所有列
        tempSelectedColumns.value = columns.value.map(col => col.prop);
      }
    };

    const confirmColumns = () => {
      if (tempSelectedColumns.value.length === 0) {
        ElMessage.warning("至少选择一列");
        return;
      }
      
      // 员工和维修工程师强制移除操作列
      if (userRole.value === 1 || userRole.value === 2) {
        const index = tempSelectedColumns.value.indexOf('operation');
        if (index > -1) {
          tempSelectedColumns.value.splice(index, 1);
        }
      }
      
      selectedColumns.value = [...tempSelectedColumns.value];
      columnsDialogVisible.value = false;
    };

    // 数据操作
    const loadData = async () => {
      try {
        const response = await getImportedDevices(queryParams.value);
        console.log(response);
        if (response.data && response.success) {
          tableData.value = response.data;
        } else {
          ElMessage.error(response.message || "获取数据失败");
        }
      } catch (error) {
        console.error("Error loading data:", error);
        ElMessage.error("数据加载失败");
      }
    };



    onMounted(loadData);

    return {
      queryParams,
      columns,
      visibleColumns,
      tableData,
      tempSelectedColumns,
      selectedColumns,
      rowHeight,
      densityDialogVisible,
      columnsDialogVisible,
      getStatusClass,
      getRowStyle,
      tableRowClassName,
      setRowHeight,
      resetColumns,
      confirmColumns,
      refreshData,
      handleQuery,
      handleReset,
    };
  },
};
</script>

<style scoped>
.my-devices {
  background-color: #f5f7fa;
  min-height: 100vh;
}

.query-section {
  background: white;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 10px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.data-section {
  background: white;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.query-form {
  display: flex;
  align-items: center;
  gap: 30px;
}

.form-item {
  display: flex;
  align-items: center;
}

.form-label {
  width: 80px;
  font-size: 14px;
  color: #606266;
}

.input-field {
  width: 220px;
}

.button-group {
  margin-left: auto;
  display: flex;
  gap: 15px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0px;
}

.toolbar {
  display: flex;
  gap: 8px;
}

/* 状态指示点 */
.status-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 8px;
}

.status-dot-green {
  background-color: #67c23a;
}

.status-dot-red {
  background-color: #f56c6c;
}

/* 弹窗样式 */
.popover-content {
  display: flex;
  flex-direction: column;
}

.popover-footer {
  margin-top: 10px;
  text-align: right;
}

.density-button {
  display: flex; /* 使用 Flexbox 布局 */
  justify-content: center; /* 水平居中 */
  margin: 10px; /* 按钮之间的间距 */
}

/* 表格样式 */
.el-table {
  width: 100% !important; /* 确保表格宽度全屏 */
  margin: 0;
}

.even-row {
  background-color: #fafafa;
}

.odd-row {
  background-color: #ffffff;
}

.empty-state {
  padding: 40px 0;
  text-align: center;
}

.empty-icon {
  font-size: 64px;
  color: #909399;
  margin-bottom: 16px;
}

.empty-text {
  color: #909399;
  margin: 0;
  font-size: 14px;
}
</style>
